import { getRandom } from 'helpers';
import * as setup from 'setup';
import Nature from '../Nature/Nature';
import Ant from './Ant';

export default class AntExplorer extends Ant {

    bridgeLeaf?: Nature;
    web: Nature;

    constructor(gameState: GameState, props?: AntProps, state?: any) {
        // Get mutation and role
        let mutation: AntExplorerMutation | undefined = undefined;
        let role: AntExplorerRole | undefined = undefined;
        if (gameState.entities.ants.explorers.created === 0) {
            role = 'first';

        } else if (gameState.unlocks.antExplorerMutation) {
            if (gameState.entities.ants.explorers.mutations === 0) { // First explorer
                mutation = (gameState.caloriesIncome - gameState.caloriesCost < 1) ? 'speed' : 'weapon'; // If player is already in trouble, make this a speed mutation
                role = 'good';

            } else if (gameState.entities.ants.explorers.mutations === 1) { // Second explorer
                mutation = 'useless';
                role = 'bad';

            } else if (gameState.entities.ants.explorers.mutations === 2 && !gameState.unlocks.river) { // Third explorer, unless river is already unlocked
                role = 'river';

            } else { // Fourth explorer and onwards

                // Check AntWorkers reachable Foods for carrier
                if (gameState.entities.ants.workers.active.length) {
                    const antColony = gameState.entities.natures.active.find(nature => nature.type === 'antColony') as Nature;
                    const maxDistance = antColony.getMaxCarryDistance();
                    if (!antColony.getClosestTarget(gameState.entities.foods.active, maxDistance)) {
                        mutation = 'carrier';
                    }
                }

                if (!mutation) {
                    if (gameState.entities.ants.explorers.mutations < 9 && gameState.turns < 400) { // Randomize 4th till 9th explorer, unless playing too long
                        mutation = getRandom([
                            'defense',
                            'carrier',
                            'speed',
                            'useless',
                            'weapon',
                        ]);

                    } else { // Either any explorer after 10th, or after certain amount of time is passed
                        mutation = 'gardening';
                        role = 'bridge';
                    }
                }
            }
        }

        // Call super
        super(gameState, 'explorer', {
            ...setup.ants.explorer,
            ...(mutation ? setup.ants.explorer.mutations[mutation] : {}),
        }, {
            ...props,
            mutation,
            role,
        }, state);

        // Initialize
        this.web = gameState.entities.natures.active.find(nature => nature.type === 'web') as Nature;
    }

    init = () => {
        this.onMove();
        if (this.gameState.unlocks.paradise) {
            this.moveToParadise();
        } else if (this.role === 'first') {
            this.wanderToWeb();
        } else if (this.role === 'river') {
            this.wanderToRiver();
        } else if (this.role === 'bridge') {
            this.checkBridge();
        } else {
            this.wander();
        }
    }

    wander = () => {
        // Check paradise
        if (this.gameState.unlocks.paradise) {
            return this.moveToParadise();
        }

        this.wanderTo(this.cell, 0, this.wander);
    }

    wanderToRiver = () => {
        // Unlock river if close enough, or wander to it
        const bridgeBlocker = this.getBridgeBlocker();
        if (this.isCloseTo(bridgeBlocker.cell, 2)) {
            this.gameState.setUnlock('river', true);
            this.wander();

        } else {
            this.wanderTo(bridgeBlocker.cell, 0.8, this.wanderToRiver);
        }
    }

    wanderToWeb = () => {
        // Wander to spider web untill death
        this.wanderTo(this.web.cell, 0.5, this.wanderToWeb);
    }

    checkBridge = () => {
        // Check paradise
        if (this.gameState.unlocks.paradise) {
            return this.moveToParadise();
        }

        if (!this.target?.active) {
            this.target = this.getBridgeBlocker();
        }

        if (this.target) {
            // Move to bridge blocker to drop off leaf, or find new leaf
            if (!this.isCloseToTarget(this.target)) {
                this.moveToTarget(this.target, this.checkBridge);
            } else if (this.bridgeLeaf) {
                this.dropBridgeLeaf();
            } else {
                this.findBridgeLeaf();
            }

        } else {
            this.wanderTo(this.cell, 0, this.checkBridge);
        }
    }

    getBridgeBlocker = () => {
        // Get next bridge blocker
        const bridgeBlockers = this.gameState.entities.natures.active.filter(nature => nature.type === 'bridgeBlocker' && nature.active && !nature.destroyed);
        bridgeBlockers.sort((a, b) => a.y < b.y ? -1 : 1);
        return bridgeBlockers[0];
    }

    findBridgeLeaf = () => {
        // Get closest leaf
        const leaves = this.gameState.entities.natures.active.filter(nature => nature.active && nature.type.startsWith('leaf'));
        this.bridgeLeaf = this.getClosestTarget(leaves);

        if (this.bridgeLeaf) {
            this.moveTo(this.bridgeLeaf.cell, 0, this.carryBridgeLeaf);
        } else {
            this.queue(this.checkBridge);
        }
    }

    carryBridgeLeaf = () => {
        // If close, pick up leaf, otherwise move to next
        if (this.bridgeLeaf?.active && this.isCloseToTarget(this.bridgeLeaf)) {
            this.bridgeLeaf.active = false;
            this.bridgeLeaf.visible = false;
            this.queue(this.checkBridge);
        } else {
            this.findBridgeLeaf();
        }
    }

    dropBridgeLeaf = () => {
        if (this.bridgeLeaf && this.target) {
            // Place leaf on bridge blocker
            this.bridgeLeaf.placeAt(this.target.cell);
            this.bridgeLeaf.visible = true;
            delete this.bridgeLeaf;

            // Remove bridge blocker
            this.gameState.removeEntity(null, null, this.target);
        }

        this.queue(this.checkBridge);
    }

    getMovingSpeed = () => {
        let movingSpeed = (this.setup.movingSpeed || 1) * (this.gameState.entities.ants.buffs.speed + 1);
        if (this.role === 'first' && this.isCloseToTarget(this.web, -1)) {
            movingSpeed *= 0.1;
        }
        return movingSpeed;
    }

}